/**
 * IndexedDB Utility for Offline Data Storage
 * Handles offline data collection entries and dropdown caching
 */

const DB_NAME = 'ABATherapyOfflineDB';
const DB_VERSION = 4; // v4: add cachedClinicalData store for offline targets/behaviors/mands

// Store names
const STORES = {
  OFFLINE_ENTRIES: 'offlineEntries',
  CACHED_DROPDOWNS: 'cachedDropdowns',
  CACHED_CHILDREN: 'cachedChildren',
  CACHED_SESSIONS: 'cachedSessions',
  SYNC_QUEUE: 'syncQueue',
  USER_SESSION: 'userSession',
  THERAPIST_PROFILES: 'therapistProfiles',
  CACHED_CLINICAL_DATA: 'cachedClinicalData'
};

// Data collection form types
export const FORM_TYPES = {
  COLD_PROBE: 'cold-probe',
  MULTI_TRIAL: 'multi-trial',
  DAILY_MAND: 'daily-mand',
  BEHAVIOR_TRACKING: 'behavior-tracking',
  ABC: 'abc',
  MAINTENANCE_PROBE: 'maintenance-probe',
  SKILL_TRACKING: 'skill-tracking',
  INJURY_REPORT: 'injury-report',
  BEHAVIOR_INTERVAL: 'behavior-interval',
  LESSON_PLAN: 'lesson-plan',
  PARENT_TRAINING: 'parent-training',
  SESSION_PLAN: 'session-plan',
  TOILETING_PROGRAM: 'toileting-program',
  SESSION_NOTES: 'session-notes',
  OT_DATA_SHEET: 'ot-data-sheet',
  SLP_DATA_SHEET: 'slp-data-sheet'
};

let db = null;

/**
 * Initialize the IndexedDB database
 */
export const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('IndexedDB error:', event.target.error);
      reject(event.target.error);
    };

    request.onsuccess = (event) => {
      db = event.target.result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Store for offline data collection entries
      if (!database.objectStoreNames.contains(STORES.OFFLINE_ENTRIES)) {
        const entriesStore = database.createObjectStore(STORES.OFFLINE_ENTRIES, {
          keyPath: 'id',
          autoIncrement: true
        });
        entriesStore.createIndex('formType', 'formType', { unique: false });
        entriesStore.createIndex('childId', 'childId', { unique: false });
        entriesStore.createIndex('createdAt', 'createdAt', { unique: false });
        entriesStore.createIndex('synced', 'synced', { unique: false });
        entriesStore.createIndex('formType_childId', ['formType', 'childId'], { unique: false });
      }

      // Store for cached dropdown values
      if (!database.objectStoreNames.contains(STORES.CACHED_DROPDOWNS)) {
        const dropdownStore = database.createObjectStore(STORES.CACHED_DROPDOWNS, {
          keyPath: 'key'
        });
        dropdownStore.createIndex('lastUpdated', 'lastUpdated', { unique: false });
      }

      // Store for cached children list
      if (!database.objectStoreNames.contains(STORES.CACHED_CHILDREN)) {
        const childrenStore = database.createObjectStore(STORES.CACHED_CHILDREN, {
          keyPath: 'id'
        });
        childrenStore.createIndex('lastUpdated', 'lastUpdated', { unique: false });
      }

      // Store for cached sessions (calendar data)
      if (!database.objectStoreNames.contains(STORES.CACHED_SESSIONS)) {
        const sessionsStore = database.createObjectStore(STORES.CACHED_SESSIONS, {
          keyPath: 'id'
        });
        sessionsStore.createIndex('date', 'date', { unique: false });
        sessionsStore.createIndex('childId', 'childId', { unique: false });
        sessionsStore.createIndex('status', 'status', { unique: false });
        sessionsStore.createIndex('lastUpdated', 'lastUpdated', { unique: false });
      }

      // Store for sync queue (pending uploads)
      if (!database.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncStore = database.createObjectStore(STORES.SYNC_QUEUE, {
          keyPath: 'id',
          autoIncrement: true
        });
        syncStore.createIndex('priority', 'priority', { unique: false });
        syncStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // Store for user session info (therapist details for offline)
      if (!database.objectStoreNames.contains(STORES.USER_SESSION)) {
        database.createObjectStore(STORES.USER_SESSION, {
          keyPath: 'key'
        });
      }

      // Store for therapist profiles (multi-therapist offline support)
      if (!database.objectStoreNames.contains(STORES.THERAPIST_PROFILES)) {
        const profileStore = database.createObjectStore(STORES.THERAPIST_PROFILES, { keyPath: 'id' });
        profileStore.createIndex('lastLoginAt', 'lastLoginAt', { unique: false });
      }

      // Version 3 upgrade: add therapistPersonId index to cachedChildren and cachedSessions
      if (event.oldVersion < 3) {
        const transaction = event.target.transaction;
        if (database.objectStoreNames.contains(STORES.CACHED_CHILDREN)) {
          const childrenStore = transaction.objectStore(STORES.CACHED_CHILDREN);
          if (!childrenStore.indexNames.contains('therapistPersonId')) {
            childrenStore.createIndex('therapistPersonId', 'therapistPersonId', { unique: false });
          }
        }
        if (database.objectStoreNames.contains(STORES.CACHED_SESSIONS)) {
          const sessionsStore = transaction.objectStore(STORES.CACHED_SESSIONS);
          if (!sessionsStore.indexNames.contains('therapistPersonId')) {
            sessionsStore.createIndex('therapistPersonId', 'therapistPersonId', { unique: false });
          }
        }
      }

      // Version 4 upgrade: add cachedClinicalData store for offline IEP targets, behaviors, mand items
      if (!database.objectStoreNames.contains(STORES.CACHED_CLINICAL_DATA)) {
        const clinicalStore = database.createObjectStore(STORES.CACHED_CLINICAL_DATA, {
          keyPath: 'childId'
        });
        clinicalStore.createIndex('cachedAt', 'cachedAt', { unique: false });
      }
    };
  });
};

/**
 * Get the database instance
 */
const getDB = async () => {
  if (!db) {
    await initDB();
  }
  return db;
};

// ============================================================================
// OFFLINE ENTRIES OPERATIONS
// ============================================================================

/**
 * Save an offline data entry
 * @param {Object} entry - The data entry to save
 * @returns {Promise<number>} - The ID of the saved entry
 */
export const saveOfflineEntry = async (entry) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.OFFLINE_ENTRIES], 'readwrite');
    const store = transaction.objectStore(STORES.OFFLINE_ENTRIES);

    const entryWithMeta = {
      ...entry,
      // Globally unique offline ID for server-side deduplication.
      // Using crypto.randomUUID() ensures no collisions across devices,
      // unlike the IndexedDB auto-increment ID which restarts per browser.
      offlineId: entry.offlineId || crypto.randomUUID(),
      createdAt: entry.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      synced: entry.synced ?? false,
      syncStatus: entry.syncStatus || 'pending', // 'pending', 'syncing', 'synced', 'failed'
      syncAttempts: entry.syncAttempts ?? 0,
      syncError: entry.syncError || null,
      lastSyncAttempt: entry.lastSyncAttempt || null
    };

    const request = store.add(entryWithMeta);

    request.onsuccess = async () => {
      // Debug: Log total count after save
      try {
        const countRequest = store.count();
        countRequest.onsuccess = () => {};
      } catch {
        // Ignore count errors
      }

      resolve(request.result);
    };

    request.onerror = () => {
      console.error('[IndexedDB] Error saving offline entry:', request.error);
      reject(request.error);
    };
  });
};

/**
 * Update an existing offline entry
 * @param {number} id - Entry ID
 * @param {Object} updates - Fields to update
 */
export const updateOfflineEntry = async (id, updates) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.OFFLINE_ENTRIES], 'readwrite');
    const store = transaction.objectStore(STORES.OFFLINE_ENTRIES);

    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const entry = getRequest.result;
      if (!entry) {
        reject(new Error('Entry not found'));
        return;
      }

      const updatedEntry = {
        ...entry,
        ...updates,
        updatedAt: new Date().toISOString()
      };

      const putRequest = store.put(updatedEntry);
      putRequest.onsuccess = () => resolve(updatedEntry);
      putRequest.onerror = () => reject(putRequest.error);
    };

    getRequest.onerror = () => reject(getRequest.error);
  });
};

/**
 * Get all offline entries for a specific form type and child
 * @param {string} formType - The form type
 * @param {number} childId - The child ID
 */
export const getOfflineEntries = async (formType, childId) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.OFFLINE_ENTRIES], 'readonly');
    const store = transaction.objectStore(STORES.OFFLINE_ENTRIES);
    const index = store.index('formType_childId');
    const request = index.getAll([formType, childId]);

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get ALL offline entries for a specific therapist (both synced and unsynced)
 * @param {string} therapistId - The therapist's user ID
 */
export const getAllOfflineEntriesForTherapist = async (therapistId) => {
  const all = await getAllOfflineEntries();
  return all.filter((entry) => entry.therapistId === therapistId);
};

/**
 * Get ALL offline entries (both synced and unsynced)
 * Use this for displaying all entries in the sync status page
 */
export const getAllOfflineEntries = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.OFFLINE_ENTRIES], 'readonly');
    const store = transaction.objectStore(STORES.OFFLINE_ENTRIES);
    const request = store.getAll();

    request.onsuccess = () => {
      const entries = request.result || [];
      resolve(entries);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get all unsynced offline entries
 * Note: We get all entries and filter in JS because IndexedDB boolean index queries
 * are unreliable across browsers
 */
export const getUnsyncedEntries = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.OFFLINE_ENTRIES], 'readonly');
    const store = transaction.objectStore(STORES.OFFLINE_ENTRIES);
    const request = store.getAll();

    request.onsuccess = () => {
      const allEntries = request.result || [];
      // Filter for unsynced entries (synced === false OR syncStatus !== 'synced')
      const unsyncedEntries = allEntries.filter((entry) => entry.synced === false || (entry.syncStatus && entry.syncStatus !== 'synced'));
      resolve(unsyncedEntries);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get unsynced entries for a specific therapist
 * @param {number|string} therapistId - The therapist ID
 */
export const getUnsyncedEntriesForTherapist = async (therapistId) => {
  const allUnsynced = await getUnsyncedEntries();
  return allUnsynced.filter((entry) => entry.therapistId === therapistId);
};

/**
 * Get unsynced entries from OTHER therapists (not the current one)
 * @param {number|string} therapistId - The current therapist ID
 */
export const getUnsyncedEntriesFromOthers = async (therapistId) => {
  const allUnsynced = await getUnsyncedEntries();
  return allUnsynced.filter((entry) => entry.therapistId && entry.therapistId !== therapistId);
};

/**
 * Get count of unsynced entries
 */
export const getUnsyncedCount = async () => {
  const entries = await getUnsyncedEntries();
  return entries.length;
};

/**
 * Get count of unsynced entries for a specific therapist
 * @param {number|string} therapistId - The therapist ID
 */
export const getUnsyncedCountForTherapist = async (therapistId) => {
  const entries = await getUnsyncedEntriesForTherapist(therapistId);
  return entries.length;
};

/**
 * Mark an entry as synced
 * @param {number} id - Entry ID
 */
export const markEntrySynced = async (id) => {
  return updateOfflineEntry(id, { synced: true, syncedAt: new Date().toISOString() });
};

/**
 * Delete an offline entry
 * @param {number} id - Entry ID
 */
export const deleteOfflineEntry = async (id) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.OFFLINE_ENTRIES], 'readwrite');
    const store = transaction.objectStore(STORES.OFFLINE_ENTRIES);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Delete all synced entries (cleanup)
 * Note: We iterate through all entries and filter in JS because IndexedDB boolean
 * index queries are unreliable across browsers
 */
export const clearSyncedEntries = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.OFFLINE_ENTRIES], 'readwrite');
    const store = transaction.objectStore(STORES.OFFLINE_ENTRIES);
    const request = store.openCursor();

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const entry = cursor.value;
        // Delete if synced is true OR syncStatus is 'synced'
        if (entry.synced === true || entry.syncStatus === 'synced') {
          store.delete(cursor.primaryKey);
        }
        cursor.continue();
      } else {
        resolve();
      }
    };

    request.onerror = () => reject(request.error);
  });
};

/**
 * Delete synced entries belonging to a specific therapist.
 * By default removes entries synced more than 24 hours ago so users can review
 * recent sync results. Pass `olderThanMs: 0` to purge ALL synced entries
 * immediately (used by the Sync Status page so synced entries don't accumulate —
 * the data is already persisted on the server once synced).
 * @param {string} therapistId - The therapist's user ID
 * @param {{olderThanMs?: number}} [opts]
 */
export const clearSyncedEntriesForTherapist = async (therapistId, { olderThanMs = 24 * 60 * 60 * 1000 } = {}) => {
  const database = await getDB();
  const cutoff = Date.now() - olderThanMs;

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.OFFLINE_ENTRIES], 'readwrite');
    const store = transaction.objectStore(STORES.OFFLINE_ENTRIES);
    const request = store.openCursor();
    let deletedCount = 0;

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const entry = cursor.value;
        if (entry.therapistId === therapistId && (entry.synced === true || entry.syncStatus === 'synced')) {
          // olderThanMs <= 0 → purge immediately; otherwise only delete entries
          // synced before the cutoff (and that carry a syncedAt timestamp).
          const syncedAt = entry.syncedAt ? new Date(entry.syncedAt).getTime() : 0;
          if (olderThanMs <= 0 || (syncedAt && syncedAt < cutoff)) {
            store.delete(cursor.primaryKey);
            deletedCount++;
          }
        }
        cursor.continue();
      } else {
        resolve(deletedCount);
      }
    };

    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear synced session updates belonging to a specific therapist.
 * By default removes entries synced more than 24 hours ago; pass `olderThanMs: 0`
 * to purge all synced session updates immediately.
 * @param {string} therapistId - The therapist's user ID
 * @param {{olderThanMs?: number}} [opts]
 */
export const clearSyncedSessionUpdatesForTherapist = async (therapistId, { olderThanMs = 24 * 60 * 60 * 1000 } = {}) => {
  const database = await getDB();
  const cutoff = Date.now() - olderThanMs;

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.SYNC_QUEUE], 'readwrite');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);
    const request = store.openCursor();
    let deletedCount = 0;

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const val = cursor.value;
        if (val.therapistId === therapistId && val.synced && val.type === 'session-update') {
          const syncedAt = val.syncedAt ? new Date(val.syncedAt).getTime() : 0;
          if (olderThanMs <= 0 || (syncedAt && syncedAt < cutoff)) {
            store.delete(cursor.primaryKey);
            deletedCount++;
          }
        }
        cursor.continue();
      } else {
        resolve(deletedCount);
      }
    };

    request.onerror = () => reject(request.error);
  });
};

// ============================================================================
// CACHED DROPDOWNS OPERATIONS
// ============================================================================

/**
 * Cache dropdown values
 * @param {string} key - Unique key for the dropdown
 * @param {Array} values - Dropdown values to cache
 */
export const cacheDropdownValues = async (key, values) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CACHED_DROPDOWNS], 'readwrite');
    const store = transaction.objectStore(STORES.CACHED_DROPDOWNS);

    const data = {
      key,
      values,
      lastUpdated: new Date().toISOString()
    };

    const request = store.put(data);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get cached dropdown values
 * @param {string} key - The dropdown key
 */
export const getCachedDropdownValues = async (key) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CACHED_DROPDOWNS], 'readonly');
    const store = transaction.objectStore(STORES.CACHED_DROPDOWNS);
    const request = store.get(key);

    request.onsuccess = () => {
      const result = request.result;
      resolve(result ? result.values : null);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get all cached dropdown keys
 */
export const getAllCachedDropdownKeys = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CACHED_DROPDOWNS], 'readonly');
    const store = transaction.objectStore(STORES.CACHED_DROPDOWNS);
    const request = store.getAllKeys();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

// ============================================================================
// CACHED CLINICAL DATA OPERATIONS (IEP targets, behaviors, mand items per child)
// ============================================================================

/**
 * Cache clinical data (IEP targets, behaviors, mand items) for a child
 * @param {string} childId - Child ID
 * @param {Object} data - Clinical data to cache
 * @param {Object} data.iepData - Raw IEP response { goals: [...] }
 * @param {Array} data.mandItems - Mand items
 * @param {Array} data.behaviors - Ref behaviors
 */
export const cacheClinicalData = async (childId, data) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CACHED_CLINICAL_DATA], 'readwrite');
    const store = transaction.objectStore(STORES.CACHED_CLINICAL_DATA);

    const record = {
      childId,
      iepData: data.iepData,
      mandItems: data.mandItems,
      behaviors: data.behaviors,
      // The IEP snapshot + per-goal targets overlay carry the human-readable
      // target NAMES and the supervisor-tracked behaviors. Without these the
      // offline page falls back to the active-targets path and renders target
      // codes (e.g. "T-mp3uw5co-...") instead of names. Persist them all.
      iepBehaviorsSnapshot: data.iepBehaviorsSnapshot,
      iepSnapshot: data.iepSnapshot,
      iepTargetsOverlay: data.iepTargetsOverlay,
      maintenanceHistory: data.maintenanceHistory,
      addTargetCtx: data.addTargetCtx,
      iepVersionId: data.iepVersionId,
      trackingTargets: data.trackingTargets,
      cachedAt: new Date().toISOString()
    };

    const request = store.put(record);
    request.onsuccess = () => {
      resolve();
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get cached clinical data for a child
 * @param {string} childId - Child ID
 * @returns {Promise<Object|null>} Cached clinical data or null
 */
export const getCachedClinicalData = async (childId) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CACHED_CLINICAL_DATA], 'readonly');
    const store = transaction.objectStore(STORES.CACHED_CLINICAL_DATA);
    const request = store.get(childId);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
};

// ============================================================================
// CACHED CHILDREN OPERATIONS
// ============================================================================

/**
 * Cache children list
 * @param {Array} children - List of children to cache
 */
export const cacheChildren = async (children) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CACHED_CHILDREN], 'readwrite');
    const store = transaction.objectStore(STORES.CACHED_CHILDREN);

    // Clear existing children first
    store.clear();

    const lastUpdated = new Date().toISOString();

    children.forEach((child) => {
      store.put({
        ...child,
        lastUpdated
      });
    });

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Get all cached children
 */
export const getCachedChildren = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CACHED_CHILDREN], 'readonly');
    const store = transaction.objectStore(STORES.CACHED_CHILDREN);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get a cached child by ID
 * @param {number} childId - The child ID
 */
export const getCachedChild = async (childId) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CACHED_CHILDREN], 'readonly');
    const store = transaction.objectStore(STORES.CACHED_CHILDREN);
    const request = store.get(childId);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
};

// ============================================================================
// CACHED SESSIONS OPERATIONS (Calendar data)
// ============================================================================

/**
 * Cache sessions list (calendar data)
 * @param {Array} sessions - List of sessions to cache
 */
export const cacheSessions = async (sessions) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CACHED_SESSIONS], 'readwrite');
    const store = transaction.objectStore(STORES.CACHED_SESSIONS);

    // Clear existing sessions first
    store.clear();

    const lastUpdated = new Date().toISOString();

    sessions.forEach((session) => {
      store.put({
        ...session,
        lastUpdated
      });
    });

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Get all cached sessions
 */
export const getCachedSessions = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CACHED_SESSIONS], 'readonly');
    const store = transaction.objectStore(STORES.CACHED_SESSIONS);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get cached sessions for a specific date
 * @param {string} date - Date string (YYYY-MM-DD)
 */
export const getCachedSessionsByDate = async (date) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CACHED_SESSIONS], 'readonly');
    const store = transaction.objectStore(STORES.CACHED_SESSIONS);
    const index = store.index('date');
    const request = index.getAll(date);

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get a cached session by ID
 * @param {number} sessionId - The session ID
 */
export const getCachedSession = async (sessionId) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CACHED_SESSIONS], 'readonly');
    const store = transaction.objectStore(STORES.CACHED_SESSIONS);
    const request = store.get(sessionId);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Update a cached session (e.g., when starting a session offline)
 * @param {number} sessionId - Session ID
 * @param {Object} updates - Fields to update
 */
export const updateCachedSession = async (sessionId, updates) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CACHED_SESSIONS], 'readwrite');
    const store = transaction.objectStore(STORES.CACHED_SESSIONS);

    const getRequest = store.get(sessionId);

    getRequest.onsuccess = () => {
      const session = getRequest.result;
      if (!session) {
        reject(new Error('Session not found'));
        return;
      }

      const updatedSession = {
        ...session,
        ...updates,
        lastUpdated: new Date().toISOString()
      };

      const putRequest = store.put(updatedSession);
      putRequest.onsuccess = () => resolve(updatedSession);
      putRequest.onerror = () => reject(putRequest.error);
    };

    getRequest.onerror = () => reject(getRequest.error);
  });
};

// ============================================================================
// USER SESSION OPERATIONS
// ============================================================================

/**
 * Save user session for offline use
 * @param {Object} user - User/therapist data
 */
export const saveUserSession = async (user) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.USER_SESSION], 'readwrite');
    const store = transaction.objectStore(STORES.USER_SESSION);

    const request = store.put({
      key: 'currentUser',
      user,
      savedAt: new Date().toISOString()
    });

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get saved user session
 */
export const getUserSession = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.USER_SESSION], 'readonly');
    const store = transaction.objectStore(STORES.USER_SESSION);
    const request = store.get('currentUser');

    request.onsuccess = () => {
      const result = request.result;
      resolve(result ? result.user : null);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear user session
 */
export const clearUserSession = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.USER_SESSION], 'readwrite');
    const store = transaction.objectStore(STORES.USER_SESSION);
    const request = store.delete('currentUser');

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if offline data is available (user has logged in at least once)
 */
export const isOfflineDataAvailable = async () => {
  try {
    // Offline mode is available if we have cached children OR a cached therapist profile
    const children = await getCachedChildren();
    if (children.length > 0) return true;
    const profiles = await getAllTherapistProfiles();
    return profiles.length > 0;
  } catch {
    return false;
  }
};

/**
 * Get storage statistics
 */
export const getStorageStats = async () => {
  const unsyncedEntries = await getUnsyncedEntries();
  const cachedChildren = await getCachedChildren();
  const dropdownKeys = await getAllCachedDropdownKeys();

  return {
    unsyncedEntriesCount: unsyncedEntries.length,
    cachedChildrenCount: cachedChildren.length,
    cachedDropdownsCount: dropdownKeys.length,
    lastSyncCheck: new Date().toISOString()
  };
};

/**
 * Clear all offline data
 */
export const clearAllOfflineData = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const storeNames = Object.values(STORES);
    const transaction = database.transaction(storeNames, 'readwrite');

    storeNames.forEach((storeName) => {
      transaction.objectStore(storeName).clear();
    });

    transaction.oncomplete = () => {
      resolve();
    };
    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Clear only data collection entries and session updates (sync queue).
 * Preserves cached reference data (children, sessions, dropdowns, clinical data, profiles).
 * Can be called from browser console: window.clearDataCollectionData()
 */
export const clearDataCollectionData = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const storesToClear = [STORES.OFFLINE_ENTRIES, STORES.SYNC_QUEUE];
    const transaction = database.transaction(storesToClear, 'readwrite');

    storesToClear.forEach((storeName) => {
      transaction.objectStore(storeName).clear();
    });

    transaction.oncomplete = () => {
      resolve({ cleared: storesToClear });
    };
    transaction.onerror = () => reject(transaction.error);
  });
};

// Expose on window for easy browser console access during development/testing
if (typeof window !== 'undefined') {
  window.clearDataCollectionData = clearDataCollectionData;
  window.clearAllOfflineData = clearAllOfflineData;
}

// ============================================================================
// SESSION STATUS UPDATES (for offline sync)
// ============================================================================

/**
 * Session update types
 */
export const SESSION_UPDATE_TYPES = {
  START: 'start-session',
  END: 'end-session',
  CANCEL: 'cancel-session',
  NO_SHOW: 'no-show'
};

/**
 * Save a session status update to the sync queue
 * @param {Object} update - The session update data
 * @param {number} update.sessionId - The session ID
 * @param {string} update.updateType - 'start-session' or 'end-session'
 * @param {string} update.status - New status ('in-progress' or 'completed')
 * @param {string} update.actualStartTime - Start time (for start updates)
 * @param {string} update.actualEndTime - End time (for end updates)
 */
export const saveSessionUpdate = async (update) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.SYNC_QUEUE], 'readwrite');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);

    // Deduplicate: check if a matching (sessionId, updateType) entry already exists
    const cursorRequest = store.openCursor();
    let existingId = null;

    cursorRequest.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const entry = cursor.value;
        if (
          entry.type === 'session-update' &&
          entry.sessionId === update.sessionId &&
          entry.updateType === update.updateType &&
          !entry.synced
        ) {
          existingId = entry.id;
          // Found a duplicate — skip adding, just resolve with existing ID
          resolve(existingId);
          return;
        }
        cursor.continue();
      } else {
        // No duplicate found — add new entry
        const queueEntry = {
          type: 'session-update',
          sessionId: update.sessionId,
          updateType: update.updateType,
          therapistId: update.therapistId || null,
          data: {
            status: update.status,
            actualStartTime: update.actualStartTime,
            actualEndTime: update.actualEndTime,
            notes: update.notes || null,
            cancellationReason: update.cancellationReason || null
          },
          createdAt: new Date().toISOString(),
          synced: false,
          syncAttempts: 0,
          priority: 1 // High priority for session updates
        };

        const addRequest = store.add(queueEntry);

        addRequest.onsuccess = () => {
          resolve(addRequest.result);
        };

        addRequest.onerror = () => {
          console.error('Error saving session update:', addRequest.error);
          reject(addRequest.error);
        };
      }
    };

    cursorRequest.onerror = () => {
      console.error('Error checking for duplicate session update:', cursorRequest.error);
      reject(cursorRequest.error);
    };
  });
};

/**
 * Get all pending session updates from the sync queue
 */
export const getPendingSessionUpdates = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.SYNC_QUEUE], 'readonly');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);
    const request = store.getAll();

    request.onsuccess = () => {
      const allItems = request.result || [];
      // Filter only session updates that are not synced
      const sessionUpdates = allItems.filter((item) => item.type === 'session-update' && !item.synced);
      resolve(sessionUpdates);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get pending session updates for a specific therapist
 * @param {string} therapistId - The therapist's user ID
 */
export const getPendingSessionUpdatesForTherapist = async (therapistId) => {
  const all = await getPendingSessionUpdates();
  return all.filter((item) => item.therapistId === therapistId);
};

/**
 * Get ALL session updates (both synced and unsynced) from the sync queue
 */
export const getAllSessionUpdates = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.SYNC_QUEUE], 'readonly');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);
    const request = store.getAll();

    request.onsuccess = () => {
      const allItems = request.result || [];
      const sessionUpdates = allItems.filter((item) => item.type === 'session-update');
      resolve(sessionUpdates);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get ALL session updates for a specific therapist (synced + unsynced)
 * @param {string} therapistId - The therapist's user ID
 */
export const getAllSessionUpdatesForTherapist = async (therapistId) => {
  const all = await getAllSessionUpdates();
  return all.filter((item) => item.therapistId === therapistId);
};

/**
 * Get count of pending session updates
 */
export const getPendingSessionUpdatesCount = async () => {
  const updates = await getPendingSessionUpdates();
  return updates.length;
};

/**
 * Mark a session update as synced
 * @param {number} id - Queue entry ID
 */
export const markSessionUpdateSynced = async (id) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.SYNC_QUEUE], 'readwrite');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);

    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const entry = getRequest.result;
      if (!entry) {
        reject(new Error('Session update not found'));
        return;
      }

      const updatedEntry = {
        ...entry,
        synced: true,
        syncedAt: new Date().toISOString()
      };

      const putRequest = store.put(updatedEntry);
      putRequest.onsuccess = () => resolve(updatedEntry);
      putRequest.onerror = () => reject(putRequest.error);
    };

    getRequest.onerror = () => reject(getRequest.error);
  });
};

/**
 * Increment sync attempts for a session update
 * @param {number} id - Queue entry ID
 */
export const incrementSessionUpdateAttempts = async (id) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.SYNC_QUEUE], 'readwrite');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);

    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const entry = getRequest.result;
      if (!entry) {
        reject(new Error('Session update not found'));
        return;
      }

      const updatedEntry = {
        ...entry,
        syncAttempts: (entry.syncAttempts || 0) + 1
      };

      const putRequest = store.put(updatedEntry);
      putRequest.onsuccess = () => resolve(updatedEntry);
      putRequest.onerror = () => reject(putRequest.error);
    };

    getRequest.onerror = () => reject(getRequest.error);
  });
};

/**
 * Delete a session update from the queue
 * @param {number} id - Queue entry ID
 */
export const deleteSessionUpdate = async (id) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.SYNC_QUEUE], 'readwrite');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear all synced session updates from the queue
 */
export const clearSyncedSessionUpdates = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.SYNC_QUEUE], 'readwrite');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);
    const request = store.openCursor();

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (cursor.value.synced && cursor.value.type === 'session-update') {
          store.delete(cursor.primaryKey);
        }
        cursor.continue();
      } else {
        resolve();
      }
    };

    request.onerror = () => reject(request.error);
  });
};

// ============================================================================
// THERAPIST PROFILE OPERATIONS (Multi-therapist offline support)
// ============================================================================

/**
 * Save/update a therapist profile for offline use
 * @param {Object} user - User object from login
 */
export const saveTherapistProfile = async (user) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.THERAPIST_PROFILES], 'readwrite');
    const store = transaction.objectStore(STORES.THERAPIST_PROFILES);

    const profile = {
      id: user.id,
      personId: user.person_id || user.id,
      username: user.username,
      name: user.name || user.displayName || user.username,
      email: user.email,
      role: user.role || 'therapist',
      avatar: user.avatar || null,
      // Prefer the server's real last-login timestamp; fall back to now only if
      // the caller didn't supply one (so the picker shows a meaningful date).
      lastLoginAt: user.lastLoginAt || user.last_login_at || new Date().toISOString(),
      orgId: user.orgId || null,
      centerId: user.centerId || null
    };

    const request = store.put(profile);
    request.onsuccess = () => resolve(profile);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get all cached therapist profiles
 * @returns {Promise<Array>} - List of therapist profiles
 */
export const getAllTherapistProfiles = async () => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.THERAPIST_PROFILES], 'readonly');
    const store = transaction.objectStore(STORES.THERAPIST_PROFILES);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Get a single therapist profile by user ID
 * @param {string} userId - The user ID
 * @returns {Promise<Object|null>}
 */
export const getTherapistProfile = async (userId) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.THERAPIST_PROFILES], 'readonly');
    const store = transaction.objectStore(STORES.THERAPIST_PROFILES);
    const request = store.get(userId);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Delete a therapist profile and their cached children/sessions
 * @param {string} userId - The user ID
 */
export const deleteTherapistProfile = async (userId) => {
  const database = await getDB();
  // First get the profile to find personId
  const profile = await getTherapistProfile(userId);
  if (!profile) return;

  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.THERAPIST_PROFILES, STORES.CACHED_CHILDREN, STORES.CACHED_SESSIONS], 'readwrite');

    // Delete the profile
    transaction.objectStore(STORES.THERAPIST_PROFILES).delete(userId);

    // Delete therapist-scoped children
    const childrenStore = transaction.objectStore(STORES.CACHED_CHILDREN);
    const childrenCursor = childrenStore.openCursor();
    childrenCursor.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (cursor.value.therapistPersonId === profile.personId) {
          childrenStore.delete(cursor.primaryKey);
        }
        cursor.continue();
      }
    };

    // Delete therapist-scoped sessions
    const sessionsStore = transaction.objectStore(STORES.CACHED_SESSIONS);
    const sessionsCursor = sessionsStore.openCursor();
    sessionsCursor.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (cursor.value.therapistPersonId === profile.personId) {
          sessionsStore.delete(cursor.primaryKey);
        }
        cursor.continue();
      }
    };

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

// ============================================================================
// THERAPIST-SCOPED CACHE OPERATIONS
// ============================================================================

/**
 * Cache children for a specific therapist (replaces only that therapist's data)
 * @param {Array} children - List of children
 * @param {string} therapistPersonId - The therapist's person_id
 */
export const cacheChildrenForTherapist = async (children, therapistPersonId) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CACHED_CHILDREN], 'readwrite');
    const store = transaction.objectStore(STORES.CACHED_CHILDREN);
    const lastUpdated = new Date().toISOString();

    // Delete existing children for this therapist using cursor
    const cursorRequest = store.openCursor();
    const toDelete = [];

    cursorRequest.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (cursor.value.therapistPersonId === therapistPersonId) {
          toDelete.push(cursor.primaryKey);
        }
        cursor.continue();
      } else {
        // All cursored — now delete old records and insert new ones
        toDelete.forEach((key) => store.delete(key));
        children.forEach((child) => {
          store.put({
            ...child,
            therapistPersonId,
            lastUpdated
          });
        });
      }
    };

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Get cached children for a specific therapist
 * @param {string} therapistPersonId - The therapist's person_id
 * @returns {Promise<Array>}
 */
export const getCachedChildrenForTherapist = async (therapistPersonId) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CACHED_CHILDREN], 'readonly');
    const store = transaction.objectStore(STORES.CACHED_CHILDREN);

    // Try using the index, fall back to getAll + filter
    if (store.indexNames.contains('therapistPersonId')) {
      const index = store.index('therapistPersonId');
      const request = index.getAll(therapistPersonId);
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    } else {
      const request = store.getAll();
      request.onsuccess = () => {
        const all = request.result || [];
        resolve(all.filter((c) => c.therapistPersonId === therapistPersonId));
      };
      request.onerror = () => reject(request.error);
    }
  });
};

/**
 * Cache sessions for a specific therapist (replaces only that therapist's data)
 * @param {Array} sessions - List of sessions
 * @param {string} therapistPersonId - The therapist's person_id
 */
export const cacheSessionsForTherapist = async (sessions, therapistPersonId) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CACHED_SESSIONS], 'readwrite');
    const store = transaction.objectStore(STORES.CACHED_SESSIONS);
    const lastUpdated = new Date().toISOString();

    // Delete existing sessions for this therapist using cursor
    const cursorRequest = store.openCursor();
    const toDelete = [];

    cursorRequest.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        if (cursor.value.therapistPersonId === therapistPersonId) {
          toDelete.push(cursor.primaryKey);
        }
        cursor.continue();
      } else {
        // All cursored — now delete old records and insert new ones
        toDelete.forEach((key) => store.delete(key));
        sessions.forEach((session) => {
          store.put({
            ...session,
            therapistPersonId,
            lastUpdated
          });
        });
      }
    };

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Upsert sessions for a therapist WITHOUT clearing previously cached ones.
 * Used while browsing the calendar online so the offline cache accumulates the
 * sessions the therapist has actually seen (keyed by id, so re-fetches update
 * in place and other months/windows are preserved).
 * @param {Array} sessions
 * @param {string} therapistPersonId - The therapist's person_id
 * @returns {Promise<void>}
 */
export const upsertSessionsForTherapist = async (sessions, therapistPersonId) => {
  if (!Array.isArray(sessions) || sessions.length === 0) return;
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CACHED_SESSIONS], 'readwrite');
    const store = transaction.objectStore(STORES.CACHED_SESSIONS);
    const lastUpdated = new Date().toISOString();

    sessions.forEach((session) => {
      if (!session?.id) return;
      store.put({ ...session, therapistPersonId, lastUpdated });
    });

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Get cached sessions for a specific therapist
 * @param {string} therapistPersonId - The therapist's person_id
 * @returns {Promise<Array>}
 */
export const getCachedSessionsForTherapist = async (therapistPersonId) => {
  const database = await getDB();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction([STORES.CACHED_SESSIONS], 'readonly');
    const store = transaction.objectStore(STORES.CACHED_SESSIONS);

    if (store.indexNames.contains('therapistPersonId')) {
      const index = store.index('therapistPersonId');
      const request = index.getAll(therapistPersonId);
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    } else {
      const request = store.getAll();
      request.onsuccess = () => {
        const all = request.result || [];
        resolve(all.filter((s) => s.therapistPersonId === therapistPersonId));
      };
      request.onerror = () => reject(request.error);
    }
  });
};

export default {
  initDB,
  FORM_TYPES,
  SESSION_UPDATE_TYPES,
  // Entries
  saveOfflineEntry,
  updateOfflineEntry,
  getOfflineEntries,
  getAllOfflineEntries,
  getAllOfflineEntriesForTherapist,
  getUnsyncedEntries,
  getUnsyncedEntriesForTherapist,
  getUnsyncedEntriesFromOthers,
  getUnsyncedCount,
  getUnsyncedCountForTherapist,
  markEntrySynced,
  deleteOfflineEntry,
  clearSyncedEntries,
  clearSyncedEntriesForTherapist,
  // Dropdowns
  cacheDropdownValues,
  getCachedDropdownValues,
  getAllCachedDropdownKeys,
  // Children
  cacheChildren,
  getCachedChildren,
  getCachedChild,
  // Sessions
  cacheSessions,
  getCachedSessions,
  getCachedSessionsByDate,
  getCachedSession,
  updateCachedSession,
  // Session Updates (sync queue)
  saveSessionUpdate,
  getPendingSessionUpdates,
  getPendingSessionUpdatesForTherapist,
  getAllSessionUpdates,
  getAllSessionUpdatesForTherapist,
  getPendingSessionUpdatesCount,
  markSessionUpdateSynced,
  incrementSessionUpdateAttempts,
  deleteSessionUpdate,
  clearSyncedSessionUpdates,
  clearSyncedSessionUpdatesForTherapist,
  // User Session
  saveUserSession,
  getUserSession,
  clearUserSession,
  // Utility
  isOfflineDataAvailable,
  getStorageStats,
  clearAllOfflineData,
  clearDataCollectionData,
  // Therapist Profiles
  saveTherapistProfile,
  getAllTherapistProfiles,
  getTherapistProfile,
  deleteTherapistProfile,
  // Therapist-scoped cache
  cacheChildrenForTherapist,
  getCachedChildrenForTherapist,
  cacheSessionsForTherapist,
  getCachedSessionsForTherapist
};
